// absolute imports
import { useMutation, useQuery } from '@apollo/client';
import { setUser as setSentryUserId } from '@sentry/react';
import {
  rootRouteId,
  useLocation,
  useRouteContext,
  useSearch,
} from '@tanstack/react-router';
import _isEqual from 'lodash/isEqual';
import _isNull from 'lodash/isNull';
import _omitBy from 'lodash/omitBy';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

// relative imports
import {
  CREATE_LEDGIBLE_USER,
  UPDATE_LEDGIBLE_USER,
} from '../../data/apollo/mutations';
import {
  GET_LEDGIBLE_USER,
  PENDING_LINK_QUERY,
} from '../../data/apollo/queries';
import {
  clearCachedOrgContext,
  readCachedOrgContext,
} from '../shared/authentication/orgResolution';
import { clearCacheForLogout } from '../shared/utilities/appUtilities';
import { getAppOrigin } from '../shared/authentication/orgSubdomain';

export const coerceStringBoolean = (value) => {
  if (typeof value !== 'string') return value;
  const normalized = value.trim().toLowerCase();
  if (normalized === 'true') return true;
  if (normalized === 'false') return false;
  return value;
};

/*
 * Federated SAML profiles can arrive with `user_metadata` as a string rather than
 * an object: SAML attribute values are strings, so an object-valued attribute
 * serializes to "[object Object]". Anything that then spreads or merges it --
 * Auth0 applying an organization invitation's `user_metadata`, the post-login
 * Action that copies it into the ID token, the account-linking Action's
 * `_.merge` -- explodes that string into numerically-keyed characters.
 *
 * Normalizing here keeps the junk out of the token lookups below and out of the
 * profile persisted on the Ledgible user, without depending on tenant-side
 * Actions we do not control in every environment.
 */
export const sanitizeUserMetadata = (metadata) => {
  if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) {
    return {};
  }

  const entries = Object.entries(metadata).filter(
    ([key]) => !/^\d+$/.test(key),
  );

  return Object.fromEntries(entries);
};

export const formatAuth0Metadata = (user) => {
  if (!user || typeof user !== 'object' || Array.isArray(user)) {
    return user;
  }

  return Object.fromEntries(
    Object.entries(user).map(([key, val]) => [
      key,
      ['email_verified', 'phone_number_verified'].includes(key)
        ? coerceStringBoolean(val)
        : val,
    ]),
  );
};

/* Format oidc-compliant custom claims from auth0 to standard Auth0User graphql type fields */
export const formatAuth0UserForDatabase = (auth0User) => {
  if (!auth0User) {
    return {};
  }

  const formattedUser = { ...auth0User };
  const namespacedProperties = Object.keys(formattedUser).filter((key) =>
    key.includes('https://dashboard.ledgible.io/', 0),
  );
  const customPropertiesToStoreOnLedgibleUser = [
    'created_at',
    'last_password_reset',
    'user_metadata',
    'ledgible_tmp_user_token',
  ];

  namespacedProperties.forEach((property) => {
    const propertyName = property.substring(30);

    if (customPropertiesToStoreOnLedgibleUser.includes(propertyName)) {
      formattedUser[propertyName] = formattedUser[property];
      delete formattedUser[property];
    }

    delete formattedUser[property];
  });

  if ('user_metadata' in formattedUser) {
    formattedUser.user_metadata = sanitizeUserMetadata(
      formattedUser.user_metadata,
    );
  }

  return formatAuth0Metadata(formattedUser);
};

/*
 * Federated subjects carry a connection segment -- `samlp|<connection>|<nameId>`,
 * `oidc|<connection>|<id>` -- while database and social identities do not. Used to
 * hide controls whose backing credential lives at the client's identity provider
 * rather than in Auth0. See docs/SSO.md for the full sub contract.
 */
export const isFederatedSub = (sub) =>
  typeof sub === 'string' &&
  (sub.startsWith('samlp|') || sub.startsWith('oidc|'));

export const getUserIdFromAuth0Sub = (sub) =>
  typeof sub === 'string' ? sub.split('|').pop() : undefined;

/*
 * Single answer to "can this identity's email be trusted?", for use anywhere the
 * raw Auth0 claims are read.
 */
export const hasVerifiedEmailClaim = (claims) =>
  isFederatedSub(claims?.sub) ||
  coerceStringBoolean(claims?.email_verified) === true;

// Creation requires an invite token; an `org_id` claim alone is not a trigger.
export const shouldCreateLedgibleUser = ({
  effectiveCreationToken,
  creationAttempted,
  creatingLedgibleUser,
}) =>
  Boolean(effectiveCreationToken) &&
  !creationAttempted &&
  !creatingLedgibleUser;

// Auth0 organization ids are `org_` + 16 alphanumeric characters.
const AUTH0_ORG_ID_PREFIX = 'org_';

/*
 * Auth0's guidance is to validate on the organization id; a name may corroborate
 * one but never stand in for it. `org_id` is required because Auth0 never emits
 * `org_name` without it, and `?organization=` accepts either form so the two are
 * compared like for like.
 *
 * Unavoidable residual: a login initiated with a name cannot detect that the
 * name has since been reassigned to another org. Generated links use ids.
 */
export const orgContextMatches = ({
  expectedOrg,
  currentOrgId,
  currentOrgName,
}) => {
  if (!expectedOrg || !currentOrgId) return false;

  return expectedOrg.startsWith(AUTH0_ORG_ID_PREFIX)
    ? expectedOrg === currentOrgId
    : expectedOrg === currentOrgName;
};

// True when the login carried org context (login-time URL/cache or token `org_id`).
export const isOrgContextedLogin = ({ currentOrgId, cachedOrgContext }) =>
  Boolean(
    currentOrgId ||
      cachedOrgContext?.organization ||
      cachedOrgContext?.invitation,
  );

/* Format Ledgible user for comparison to auth0 id token claims (user) value */
const formatLedgibleUserForProfileComparison = (ledgibleUser, subId) => {
  const formattedUser = _omitBy(ledgibleUser.profile, _isNull);
  formattedUser.user_metadata = _omitBy(formattedUser.user_metadata, _isNull);
  formattedUser.nickname = ledgibleUser.profile.username;
  formattedUser.name = ledgibleUser.profile.email;
  formattedUser.sub = subId;

  delete formattedUser.username;
  delete formattedUser.__typename;
  delete formattedUser.user_metadata.__typename;

  return formattedUser;
};

// implement context
export const LedgibleAuthContext = createContext();
export const useLedgibleAuth = () => useContext(LedgibleAuthContext);

export const LedgibleAuthProvider = ({ children }) => {
  const { auth0 = {} } = useRouteContext({
    from: rootRouteId,
  });
  const { pathname } = useLocation();
  const search = useSearch({ strict: false });
  const { tmpUserToken, codeInviteToken } = search;
  const userCreationToken = tmpUserToken || codeInviteToken;
  const [idTokenClaims, setIdTokenClaims] = useState(null);

  // Ensure auth0 context is properly populated
  // If auth0 is empty object or missing required properties, treat as not ready
  const hasAuth0Context =
    auth0 && typeof auth0 === 'object' && 'isLoading' in auth0;

  const {
    isAuthenticated = false,
    isLoading = true,
    user: auth0User,
    logout: auth0Logout,
  } = hasAuth0Context ? auth0 : {};

  const formattedAuth0User = useMemo(
    () =>
      formatAuth0UserForDatabase(
        idTokenClaims
          ? Object.fromEntries(
              Object.keys(auth0User)
                .filter((key) => key in idTokenClaims)
                .map((key) => [key, idTokenClaims[key]]),
            )
          : auth0User,
      ),
    [auth0User, idTokenClaims],
  );

  // Invited org members carry their creation token in user_metadata, not the
  // URL; fall back to it when no query-param token is present.
  const metadataCreationToken =
    formattedAuth0User?.user_metadata?.ledgible_tmp_user_token;
  const effectiveCreationToken = userCreationToken || metadataCreationToken;

  useEffect(() => {
    if (hasAuth0Context && auth0.getIdTokenClaims) {
      (async () => {
        try {
          const tokenClaims = await auth0.getIdTokenClaims();
          setIdTokenClaims(tokenClaims);
        } catch (error) {
          console.error('Error getting id token claims:', error);
        }
      })();
    }
  }, [auth0, hasAuth0Context]);

  const currentOrgId = idTokenClaims?.org_id;
  const currentOrgName = idTokenClaims?.org_name;
  const isFederatedIdentity = isFederatedSub(
    idTokenClaims?.sub || auth0User?.sub,
  );

  const [orgValidationError, setOrgValidationError] = useState(null);
  useEffect(() => {
    if (!isAuthenticated || !idTokenClaims) return;

    // Validate against the cached login-time org, not the live URL, so a crafted
    // `?organization=` can't force a logout for an org never requested.
    const { organization: expectedOrg } = readCachedOrgContext() || {};
    if (!expectedOrg) return;

    const matches = orgContextMatches({
      expectedOrg,
      currentOrgId,
      currentOrgName,
    });

    if (!matches) {
      const received = currentOrgId || currentOrgName || null;
      console.warn(
        '[LedgibleAuthProvider] Token org claim does not match expected org context',
        { expectedOrg, currentOrgId, currentOrgName },
      );
      // Functional update avoids a self-dependency that would loop the effect.
      setOrgValidationError((prev) =>
        prev &&
        prev.expectedOrg === expectedOrg &&
        prev.receivedOrg === received
          ? prev
          : { expectedOrg, receivedOrg: received },
      );
    } else {
      // Matched: clear the login-time context so it can't re-trigger later.
      clearCachedOrgContext();
      setOrgValidationError((prev) => (prev ? null : prev));
    }
  }, [isAuthenticated, idTokenClaims, currentOrgId, currentOrgName]);

  const creationAttempted = useRef(false);
  const [userCreationError, setUserCreationError] = useState(null);
  const [orgInviteRequired, setOrgInviteRequired] = useState(null);

  const [updateLedgibleUser] = useMutation(UPDATE_LEDGIBLE_USER, {
    variables: {
      auth0User: formattedAuth0User,
    },
  });

  const [createLedgibleUser, { loading: creatingLedgibleUser }] = useMutation(
    CREATE_LEDGIBLE_USER,
    {
      variables: {
        auth0User: formattedAuth0User,
        token: effectiveCreationToken,
      },
      refetchQueries: () => [GET_LEDGIBLE_USER],
      onCompleted: () => {
        setUserCreationError(null);
      },
      onError: (error) => {
        // Release the guard so the attempt can retry without a page reload.
        creationAttempted.current = false;
        setUserCreationError(error);
      },
    },
  );

  // Use idTokenClaims if available, otherwise fall back to auth0User
  // This handles the case where idTokenClaims hasn't been fetched yet
  const userClaims = idTokenClaims || auth0User;

  const emailIsVerified = hasVerifiedEmailClaim(userClaims);

  const skipQuery =
    isLoading ||
    !isAuthenticated ||
    (!emailIsVerified && pathname !== '/verify-email');

  const {
    client,
    loading: loadingUser,
    data: { user } = {},
    refetch: refetchUser,
  } = useQuery(GET_LEDGIBLE_USER, {
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
    onCompleted: ({ user: result } = {}) => {
      if (result) {
        setSentryUserId(result.id);
        // diff ledgible user with auth0 user and udpate if different
        const ledgibleUserFormatted = formatLedgibleUserForProfileComparison(
          result,
          formattedAuth0User.sub,
        );

        if (!_isEqual(ledgibleUserFormatted, formattedAuth0User)) {
          updateLedgibleUser();
        }
      } else if (
        shouldCreateLedgibleUser({
          effectiveCreationToken,
          creationAttempted: creationAttempted.current,
          creatingLedgibleUser,
        })
      ) {
        creationAttempted.current = true;
        createLedgibleUser();
      } else if (
        isOrgContextedLogin({
          currentOrgId,
          cachedOrgContext: readCachedOrgContext(),
        })
      ) {
        // Uninvited org user: authenticated with org context but no record/invite.
        setOrgInviteRequired((prev) =>
          prev && prev.orgId === currentOrgId
            ? prev
            : { orgId: currentOrgId, orgName: currentOrgName },
        );
      }
    },
    skip: skipQuery,
  });

  // Query for pending link by email to get application type for account creation
  // Must come after GET_LEDGIBLE_USER query so we can reference user in skip condition
  const {
    data: { checkPendingLinkCodeInvite: pendingLinkData } = {},
    loading: loadingPendingLink,
  } = useQuery(PENDING_LINK_QUERY, {
    variables: {
      email: auth0User?.email,
    },
    skip: !auth0User?.email || !hasVerifiedEmailClaim(auth0User),
    fetchPolicy: 'network-only',
  });

  // Use application type from pending link query
  const pendingApplicationTypeId = pendingLinkData?.clientApplicationTypeId;

  const loadingAuth = useMemo(
    () =>
      isLoading || loadingUser || creatingLedgibleUser || loadingPendingLink,
    [creatingLedgibleUser, isLoading, loadingUser, loadingPendingLink],
  );

  /**
   * local logout function to handle logout call to auth0 plus session cleanup and append any other stuff
   * @param {Object} [args] - function arguments
   * @param {Object} [args.auth0LogoutOptions={}] - options for auth0 logout function
   * @param {Object} [args.auth0LogoutOptions.logoutParams={}] - options for auth0 logout function
   * @param {Boolean} [args.auth0LogoutOptions.logoutParams.redirectTo] - where to send user after auth0 logout, must be registered in Auth0 Allowed Logout URLs
   * @param {Boolean} [args.auth0LogoutOptions.logoutParams.federated] - attempts logout of upstream identity provider, if supported
   * @param {Function} [args.auth0LogoutOptions.openUrl] - optional function to handle callback manually
   * @param {String} [args.auth0LogoutOptions.clientId] - optional clientId of app to logout from, defaults to instantiated Auth0 SDK client id
   * @param {Object} [args.queryParameters={}] - optional object to transpose query parameters onto returnTo URL
   */
  const logout = useCallback(
    async ({ auth0LogoutOptions = {}, queryParameters = {} } = {}) => {
      const { logoutParams: { returnTo, ...restLogoutParams } = {}, ...rest } =
        auth0LogoutOptions;

      /*
       * Send an org-scoped session back to its own sign-in page. Without Domain
       * Discovery the organization cannot be inferred from the email alone, so a
       * plain `/login` would drop the user on the generic page and strand them
       * outside their org. Only applied to the default destination: callers that
       * pass an explicit returnTo (the invite-required and org-mismatch guards)
       * have already decided where the user goes.
       *
       * Auth0 ignores query strings when validating Allowed Logout URLs, so this
       * needs no additional entry.
       */
      const orgQueryParameters =
        currentOrgId && !returnTo ? { organization: currentOrgId } : {};

      const logoutCallbackUrl = queryString.stringifyUrl({
        url: returnTo || `${getAppOrigin()}/login`,
        query: {
          ...orgQueryParameters,
          ...queryParameters,
        },
      });

      const logoutOptions = {
        logoutParams: {
          returnTo: rest.openUrl ? null : logoutCallbackUrl,
          ...restLogoutParams,
        },
        ...rest,
      };

      await clearCacheForLogout(client);
      clearCachedOrgContext();
      await auth0Logout(logoutOptions);
    },
    [client, auth0Logout, currentOrgId],
  );

  // Re-runs the mutation directly: Apollo 3.13.8's refetch() does not re-fire
  // useQuery's onCompleted, so routing the retry through the query would clear
  // the error and strand the user on a blank dashboard with nothing shown.
  const retryUserCreation = useCallback(() => {
    setUserCreationError(null);
    creationAttempted.current = true;

    return createLedgibleUser();
  }, [createLedgibleUser]);

  // memoize values
  const ledgibleAuthValues = useMemo(
    () => ({
      user,
      refetchUser,
      loadingAuth,
      isAuthenticated,
      applicationTypeIdForAccountCreation: pendingApplicationTypeId,
      logout,
      currentOrgId,
      currentOrgName,
      isFederatedIdentity,
      orgValidationError,
      orgInviteRequired,
      userCreationError,
      retryUserCreation,
    }),
    [
      user,
      loadingAuth,
      isAuthenticated,
      logout,
      refetchUser,
      pendingApplicationTypeId,
      currentOrgId,
      currentOrgName,
      isFederatedIdentity,
      orgValidationError,
      orgInviteRequired,
      userCreationError,
      retryUserCreation,
    ],
  );

  return (
    <LedgibleAuthContext.Provider value={ledgibleAuthValues}>
      {children}
    </LedgibleAuthContext.Provider>
  );
};

export const LedgibleAuthConsumer = LedgibleAuthContext.Consumer;

LedgibleAuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
